import { useState, useCallback, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { usePermissions } from './use-permissions'
import { tenantService, type Tenant } from '@/services/tenant'
import { storage } from '@/shared'

export type { Tenant }

// Feature flag para usar dados mock enquanto backend não está pronto
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_TENANT === 'true'

// Dados mock para desenvolvimento
const MOCK_TENANTS: Tenant[] = [
  { id: '1', name: 'Academia Tigre', plan: 'Pro' },
  { id: '2', name: 'CT Leão Dourado', plan: 'Pro' },
  { id: '3', name: 'Team Guerreiros', plan: 'Free' },
  { id: '4', name: 'Dojo Samurai', plan: 'Pro' },
  { id: '5', name: 'Fight House MMA', plan: 'Pro' },
  { id: '6', name: 'BJJ Academy', plan: 'Free' },
]

const MOCK_USER_TENANT: Tenant = { id: '1', name: 'Academia Tigre', plan: 'Pro' }

async function fetchSuperAdminTenants(): Promise<Tenant[]> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 100))

    return MOCK_TENANTS
  }

  return tenantService.listForSuperAdmin()
}

async function fetchUserTenant(): Promise<Tenant> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 100))

    return MOCK_USER_TENANT
  }

  return tenantService.getCurrentUserCompany()
}

// Query keys que dependem do tenant atual e precisam ser invalidadas ao trocar
const TENANT_DEPENDENT_QUERY_KEYS = [
  'students',
  'plans',
  'modalities',
  'users',
  'company',
  'dashboard',
] as const

export function useTenant() {
  const { user, userRoles } = useAuth()
  const { isSuper } = usePermissions()
  const queryClient = useQueryClient()

  // Super-admin OU admin de empresa pode trocar entre empresas
  const canSwitchTenant = isSuper || userRoles.includes('admin')

  // Inicializa do storage se existir
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(storage.getTenantId())

  // Lista de empresas para admin/super-admin
  const { data: availableTenants = [], isLoading: isLoadingTenants } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: fetchSuperAdminTenants,
    enabled: canSwitchTenant,
  })

  // Empresa do usuário atual (para não admins)
  const { data: userTenant, isLoading: isLoadingUserTenant } = useQuery({
    queryKey: ['user-tenant'],
    queryFn: fetchUserTenant,
    enabled: !canSwitchTenant && !!user,
  })

  // Calcula o tenant atual baseado no estado
  const currentTenant = useMemo(() => {
    if (!canSwitchTenant) {
      return userTenant
    }

    // Se tem um ID selecionado, tenta encontrar nos disponíveis
    if (selectedTenantId) {
      const found = availableTenants.find((t) => t.id === selectedTenantId)

      if (found) return found
    }

    // Fallback para o primeiro disponível
    return availableTenants[0]
  }, [canSwitchTenant, userTenant, selectedTenantId, availableTenants])

  // Sincroniza o storage quando o tenant muda
  useEffect(() => {
    if (canSwitchTenant && currentTenant) {
      storage.setTenantId(currentTenant.id)
    }
  }, [canSwitchTenant, currentTenant])

  // Limpa o tenantId do storage quando não pode mais trocar
  useEffect(() => {
    if (!canSwitchTenant) {
      storage.removeTenantId()
    }
  }, [canSwitchTenant])

  const switchTenant = useCallback(
    (tenantId: string) => {
      if (!canSwitchTenant) return

      setSelectedTenantId(tenantId)
      storage.setTenantId(tenantId)

      // Invalida queries que dependem do tenant
      TENANT_DEPENDENT_QUERY_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })
    },
    [canSwitchTenant, queryClient],
  )

  return {
    currentTenant,
    availableTenants,
    switchTenant,
    canSwitchTenant,
    isLoading: isLoadingTenants || isLoadingUserTenant,
  }
}
